import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table, Empty, Segmented } from "antd";
import { Line } from "@ant-design/charts";
import SectionLoader from "../Loaders/SectionLoader";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import moment from "moment";
import { get_language, translate } from "../../locale";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import SelfMeasureServices from "../../services/SelfMeasuerServices";
import { useParams } from "react-router-dom";
import { TableHelper } from "../../helpers/TableHelper";
import RangeForm from "./RangeForm";

const TemperatureCharts = () => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("graph");
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(7, "days"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const { username } = useParams();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const [configGraph, setConfigGraph] = useState({
    data: [{ year: moment().format("DD/MM/YYYY HH:mm"), value: 0 }],
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",

    yAxis: {
      max: 40,
      min: 34,
      tickInterval: 1,
      title: {
        text: "ºC",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 0,
    },
  });

  const [dataList, setDataList] = useState([]);

  const columnsList = [
    {
      title: "Fecha",
      dataIndex: "measure_date",
      key: "measure_date",
      render: (_, record) => moment(record.measure_date).format("DD/MM/YYYY"),
      sorter: { multiple: 1 },
    },
    {
      title: "Hora",
      dataIndex: "measure_time",
      key: "measure_time",
      sorter: { multiple: 2 },
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (_, record) =>
        record.mode === "automatic" ? "Automático" : "Manual",
      sorter: { multiple: 3 },
    },
    {
      title: "ºC",
      dataIndex: "value",
      key: "value",
      render: (_, record) => {
        return `${record.temperatura}`;
      },
      sorter: { multiple: 4 },
    },
  ];

  useEffect(() => {
    loadGraph();
    loadTotalGraph();
  }, [JSON.stringify(tableParams)]);

  const loadGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "temperature",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const list = res.data.controls.map((i) => {
          const value = i.answers
            .filter((i) => i.temperatura !== undefined)
            .pop();
          return {
            measure_date: i.measure_date,
            measure_time: i.measure_time,
            temperatura: value.temperatura,
            unit: value.unit,
            mode: i.mode,
          };
        });
        setDataList(list);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  // Load ALL data in the graph
  const loadTotalGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: 1000,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "temperature",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.controls.map((i) => ({
          value: parseFloat(
            i.answers.filter((i) => i.temperatura !== undefined).pop()
              .temperatura
          ),
          year: moment(`${i.measure_date} ${i.measure_time}`)
            .tz("utc")
            .local()
            .format("DD/MM/YYYY HH:mm"),
        }));
        setConfigGraph((v) => ({ ...v, data: data.reverse() }));
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  const handleChangeItem = (value) => {
    setSelectedGraph(value);
    switch (value) {
      case "graph":
        loadTotalGraph();
        loadGraph();
        break;
      case "list":
        loadGraph();
        break;
      case "range":
        break;
    }
  };
  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
  };
  const handleSearch = (e) => {
    setSearching(true);
    loadTotalGraph();
    loadGraph();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataList([]);
      setConfigGraph([]);
    }
  };

  return (
    <>
      {selectedGraph !== "range" && (
        <Form onFinish={handleSearch}>
          <Row className="mt-6 mb-14 relative z-10" gutter={[40, 10]}>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha desde")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateFrom}
                  onChange={(e) => onChangeDatePicker("dateFrom", e)}
                />
              </div>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha hasta")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateTo}
                  onChange={(e) => onChangeDatePicker("dateTo", e)}
                />
              </div>
            </Col>
            <Col
              xs={{ span: 24 }}
              md={{ span: 6 }}
              className="text-right md:text-left"
            >
              <Button
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
                icon={
                  searching ? (
                    <LoadingOutlined />
                  ) : (
                    <SearchOutlined style={{ position: "relative" }} />
                  )
                }
              >
                {translate("Buscar")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
      <Row className="mb-14">
        <Col>
          <Segmented
            options={[
              { value: "graph", label: "Gráfico" },
              { value: "list", label: "Listado" },
              { value: "range", label: "Rangos" },
            ]}
            onChange={handleChangeItem}
          />
        </Col>
      </Row>
      <Row className="relative mb-14">
        {loading && <SectionLoader />}

        {selectedGraph === "graph" && (
          <Col span={24}>
            <Line {...configGraph} />
          </Col>
        )}

        {selectedGraph === "list" && (
          <Col span={24}>
            <Table
              rowKey={(i) => `${i.measure_date} ${i.measure_time}`}
              columns={columnsList}
              dataSource={dataList}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
            />
          </Col>
        )}
        {selectedGraph === "range" && (
          <Col span={24}>
            <Row gutter={[48, 48]}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "temperatura",
                    max_value_alert: 40,
                    max_value: 37,
                    min_value: 36,
                    min_value_alert: 35,
                  }}
                  title={translate("Rango de Temperatura")}
                />
              </Col>

              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "register_temperature",
                    max_value_alert: 30,
                    max_value: 7,
                  }}
                  title={translate("Días sin registros de Temperatura")}
                  onlyMax={true}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export default TemperatureCharts;
